'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { Stack, TextField, Box, Typography, Chip, Fade } from '@mui/material';
import { CheckCircle, Error as ErrorIcon, Save } from '@mui/icons-material';
import { useMediaBrowserV2 } from '@/app/features/shared/media-browser-v2/context/MediaBrowserV2Context';
import { SaveStatus } from '@/app/features/shared/media-browser-v2/types/types';
import { useDebounce } from 'use-debounce';

const STATUS_VISIBILITY_MS = 5000;

export default function MediaMetadataEditor() {
    const { selectedMedia, updateMetadata } = useMediaBrowserV2();

    const [name, setName] = useState('');
    const [caption, setCaption] = useState('');
    const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
    const [saveStatusMediaId, setSaveStatusMediaId] = useState<string | null>(null);
    const [hasUserEdited, setHasUserEdited] = useState(false);
    const [lastSavedName, setLastSavedName] = useState('');
    const [lastSavedCaption, setLastSavedCaption] = useState('');

    const [debouncedName] = useDebounce(name, 800);
    const [debouncedCaption] = useDebounce(caption, 800);

    const selectedMediaId = selectedMedia
        ? ('documentId' in selectedMedia ? selectedMedia.documentId : selectedMedia.id)
        : null;

    const selectedMediaIdRef = useRef<string | null>(selectedMediaId);
    const previousSelectedMediaIdRef = useRef<string | null>(null);
    const statusTimeoutRef = useRef<number | null>(null);
    const saveRequestIdRef = useRef(0);
    const isSelectionSyncingRef = useRef(false);

    const clearStatusTimeout = useCallback(() => {
        if (statusTimeoutRef.current !== null) {
            window.clearTimeout(statusTimeoutRef.current);
            statusTimeoutRef.current = null;
        }
    }, []);

    const showTemporaryStatus = useCallback((status: SaveStatus, mediaId: string) => {
        setSaveStatusMediaId(mediaId);
        setSaveStatus(status);
        clearStatusTimeout();
        statusTimeoutRef.current = window.setTimeout(() => {
            setSaveStatus('idle');
            setSaveStatusMediaId(null);
        }, STATUS_VISIBILITY_MS);
    }, [clearStatusTimeout]);

    useEffect(() => {
        selectedMediaIdRef.current = selectedMediaId;
    }, [selectedMediaId]);

    // Initialize values only when selected media ID changes
    useEffect(() => {
        if (previousSelectedMediaIdRef.current === selectedMediaId) {
            return;
        }

        previousSelectedMediaIdRef.current = selectedMediaId;
        clearStatusTimeout();
        isSelectionSyncingRef.current = true;

        if (!selectedMedia) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setName('');
            setCaption('');
            setLastSavedName('');
            setLastSavedCaption('');
            setSaveStatus('idle');
            setSaveStatusMediaId(null);
            setHasUserEdited(false);
            return;
        }

        const newName = selectedMedia.name || '';
        const newCaption = selectedMedia.caption || '';
        setName(newName);
        setCaption(newCaption);
        setLastSavedName(newName);
        setLastSavedCaption(newCaption);
        setSaveStatus('idle');
        setSaveStatusMediaId(null);
        setHasUserEdited(false);
    }, [selectedMediaId, selectedMedia, clearStatusTimeout]);

    useEffect(() => {
        return () => {
            clearStatusTimeout();
        };
    }, [clearStatusTimeout]);

    // Auto-save when debounced values change
    useEffect(() => {
        if (!selectedMediaId) return;
        if (!hasUserEdited) return;

        if (isSelectionSyncingRef.current) {
            const isSynced = debouncedName === lastSavedName && debouncedCaption === lastSavedCaption;
            if (isSynced) {
                isSelectionSyncingRef.current = false;
            }
            return;
        }

        // Check if values actually changed from last save
        const nameChanged = debouncedName !== lastSavedName;
        const captionChanged = debouncedCaption !== lastSavedCaption;

        if (!nameChanged && !captionChanged) return;

        const requestId = ++saveRequestIdRef.current;
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setSaveStatusMediaId(selectedMediaId);
        setSaveStatus('saving');
        clearStatusTimeout();

        void (async () => {
            try {
                await updateMetadata(selectedMediaId, {
                    name: debouncedName,
                    caption: debouncedCaption,
                });

                if (requestId !== saveRequestIdRef.current || selectedMediaIdRef.current !== selectedMediaId) {
                    return;
                }

                setLastSavedName(debouncedName);
                setLastSavedCaption(debouncedCaption);
                showTemporaryStatus('success', selectedMediaId);
            } catch (error) {
                console.error('Save error:', error);
                if (requestId !== saveRequestIdRef.current || selectedMediaIdRef.current !== selectedMediaId) {
                    return;
                }
                showTemporaryStatus('error', selectedMediaId);
            }
        })();
    }, [
        debouncedName,
        debouncedCaption,
        hasUserEdited,
        lastSavedName,
        lastSavedCaption,
        selectedMediaId,
        updateMetadata,
        clearStatusTimeout,
        showTemporaryStatus,
    ]);

    const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setHasUserEdited(true);
        setName(e.target.value);
    }, []);

    const handleCaptionChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setHasUserEdited(true);
        setCaption(e.target.value);
    }, []);

    if (!selectedMedia) {
        return (
            <Box
                className="flex min-h-[200px] items-center justify-center p-6"
            >
                <Typography variant="body2" color="text.disabled" align="center">
                    Wähle ein Medium aus, um die Metadaten zu bearbeiten
                </Typography>
            </Box>
        );
    }

    const isPending = 'uploadStatus' in selectedMedia;
    const visibleStatus = saveStatusMediaId === selectedMediaId ? saveStatus : 'idle';

    return (
        <Box className="p-4">
            <Stack spacing={2}>
                {/* Header with Save Status */}
                <Stack spacing={1}>

                <Box className="flex items-center justify-between">
                    <Typography variant="subtitle1" fontWeight={600}>
                        Metadaten
                    </Typography>
                    <SaveStatusIndicator status={visibleStatus} />
                </Box>
                    {!isPending && (
                        <Typography variant="overline" fontSize="small">
                            Änderungen werden automatisch gespeichert
                        </Typography>
                    )}
                </Stack>

                {/* Name Field */}
                <TextField
                    label="Name"
                    value={name}
                    onChange={handleNameChange}
                    fullWidth
                    required
                    size="small"
                    helperText={isPending && "Wird beim Upload gespeichert"}
                />

                {/* Caption Field */}
                <TextField
                    label="Bildunterschrift"
                    value={caption}
                    onChange={handleCaptionChange}
                    fullWidth
                    multiline
                    rows={4}
                    size="small"
                    placeholder="Optionale Beschreibung..."
                    helperText={isPending && "Wird beim Upload gespeichert"}
                />
            </Stack>
        </Box>
    );
}

function SaveStatusIndicator({ status }: { status: SaveStatus }) {
    if (status === 'idle') {
        return <Box className="min-w-[140px]" />;
    }

    return (
        <Fade in timeout={300}>
            <Box>
                {status === 'saving' && (
                    <Chip
                        icon={<Save sx={{ animation: 'pulse 1.5s ease-in-out infinite' }} />}
                        label="Speichern..."
                        size="small"
                        color="default"
                        variant="outlined"
                    />
                )}

                {status === 'success' && (
                    <Chip
                        icon={<CheckCircle />}
                        label="Gespeichert"
                        size="small"
                        color="success"
                        variant="filled"
                        sx={{
                            fontWeight: 600,
                            animation: 'fadeIn 0.3s ease-in-out',
                        }}
                    />
                )}

                {status === 'error' && (
                    <Chip
                        icon={<ErrorIcon />}
                        label="Fehler beim Speichern"
                        size="small"
                        color="error"
                        variant="filled"
                        sx={{
                            fontWeight: 600,
                            animation: 'fadeIn 0.3s ease-in-out',
                        }}
                    />
                )}
            </Box>
        </Fade>
    );
}
